<template>
  <div>
    <component
      :is="loadField(field.field)"
      v-if="!isNested"
      :ref="field.key"
      :disabled-field="disabledField"
      :opts="fieldOptions"
      :custom-errors="field.field === 'json' ? jsonErrors : []"
      @input="handleInput"
    />
    <!-- JSON validation errors now displayed through standard Vuelidate system -->
    <v-card v-else-if="isArray">
      <v-card-title>
        {{ field.name[$i18n.locale] }}
      </v-card-title>
      <v-flex>
        <v-row
          v-for="(answers, rowId) in answersList"
          :key="rowId"
          class="mb-0"
          :class="{'mb-8': answersList.length-1 !== rowId}"
        >
          <v-col>
            <v-row
              style="border-bottom: 2px solid transparent"
              class="align-center"
            >
              <v-col
                v-for="(childField, i) in field.children"
                :key="i"
                :cols="getColumnSize(field.children.length)"
              >
                <NestedField
                  :ref="childField.key"
                  :values="answers || {}"
                  :field="childField"
                  :validation-error="validationError"
                  @input="updateNestedValue(rowId, childField.key, $event)"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <div class="d-flex justify-space-between">
          <v-btn
            outlined
            @click="addAnswer()"
          >
            <v-icon> mdi-plus-circle-outline </v-icon>
          </v-btn>
          <v-btn
            v-if="answersList.length>1"
            outlined
            @click="removeAnswer()"
          >
            <v-icon>  mdi-bucket-outline </v-icon>
          </v-btn>
        </div>
      </v-flex>
    </v-card>
  </div>
</template>
<script>
import MdText from "@/components/ui-2/Text.vue";
import MdTextarea from "@/components/ui-2/Textarea.vue";
import MdSelect from "@/components/ui-2/Select.vue";
import MdCheckbox from "@/components/ui-2/Checkbox.vue";
import { validationMixin } from "vuelidate";

// JSON validator
const validJson = (value) => {
  if (!value || value.trim() === "") return true; // Empty values are allowed
  try {
    JSON.parse(value);
    return true;
  } catch (error) {
    return false;
  }
};

export default {
  name: "NestedField",
  components: {
    MdText,
    MdSelect,
    MdCheckbox,
    MdTextarea,
  },
  mixins: [validationMixin],
  props: {
    field: {
      type: Object,
      required: true,
    },
    values: {
      type: [Object, Array],
      required: true,
    },
    disabledField: {
      type: Boolean,
      default: false
    },
    validationError: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      answersList: [{}],
      jsonValue: "", // For storing current JSON field value
    };
  },
  computed: {
    fieldOptions() {
      // For JSON fields, always use jsonValue
      if (this.field.field === "json") {
        return { ...this.field, value: this.jsonValue };
      }
      
      // For other fields, use values
      const value = this.values[this.field.key];
      return typeof value === 'object' && !Array.isArray(value) && value !== null
          ? { ...this.field, value: value.value }
          : { ...this.field, value: value };
    },
    isNested() {
      return this.field.field === "object";
    },
    isArray() {
      return this.field.children;
    },
    loadField() {
      return (value) => {
        // If field type is json, display as textarea
        if (value === "json") {
          return "md-textarea";
        }
        return "md-" + value;
      };
    },
    jsonValidationError() {
      if (this.field.field === "json" && this.fieldOptions.value && this.fieldOptions.value.trim() !== "") {
        try {
          JSON.parse(this.fieldOptions.value);
          return null; // Valid JSON
        } catch (error) {
          return this.$t('globals.invalidJson', { field: this.field.name[this.$i18n.locale] || this.field.key });
        }
      }
      return null;
    },
    jsonErrors() {
      const errors = [];
      if (!this.$v.jsonValue.$dirty) return errors;
      !this.$v.jsonValue.validJson && errors.push(this.$t('globals.invalidJson', { field: this.field.name[this.$i18n.locale] || this.field.key }));
      return errors;
    },
    fieldOptionsWithErrors() {
      const options = { ...this.fieldOptions };
      
      // For JSON fields, add validation errors
      if (this.field.field === "json") {
        options.errorMessages = this.jsonErrors;
      }
      
      return options;
    },
  },
  watch: {
    values: {
      handler(val) {
        const childrenAnswers = val[this.field.key];
        if (this.isArray && childrenAnswers && childrenAnswers.length) {
          this.answersList = childrenAnswers;
        }
        
        // Update jsonValue when values change for JSON fields
        if (this.field.field === "json") {
          const value = val[this.field.key];
          
          if (typeof value === 'object' && value !== null) {
            this.jsonValue = JSON.stringify(value, null, 2);
          } else if (typeof value === 'string' && value.trim() !== "") {
            try {
              const parsed = JSON.parse(value);
              this.jsonValue = JSON.stringify(parsed, null, 2);
            } catch (error) {
              this.jsonValue = value;
            }
          } else {
            this.jsonValue = value || "";
          }
        }
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    // Initialize jsonValue for JSON fields
    if (this.field.field === "json") {
      const value = this.values[this.field.key];
      
      if (typeof value === 'object' && value !== null) {
        this.jsonValue = JSON.stringify(value, null, 2);
      } else if (typeof value === 'string' && value.trim() !== "") {
        try {
          const parsed = JSON.parse(value);
          this.jsonValue = JSON.stringify(parsed, null, 2);
        } catch (error) {
          this.jsonValue = value;
        }
      } else {
        this.jsonValue = value || "";
      }
    }
  },
  methods: {
    handleInput(value) {
      // If field type is json, update jsonValue and trigger validation
      if (this.field.field === "json") {
        this.jsonValue = value;
        this.$v.jsonValue.$touch();
      }
      this.$emit('input', value);
    },
    updateNestedValue(rowId, key, value) {
      this.$set(this.answersList[rowId], key, value);
      this.$emit('input', this.answersList);
    },
    getColumnSize(childrenCount) {
      // On mobile devices, always full width
      if (this.$vuetify.breakpoint.name === 'xs') {
        return 12;
      }
      
      // On desktop: if 3 fields, then 4 columns each (in one row)
      if (childrenCount === 3) {
        return 4;
      }
      
      // Default 6 columns (2 fields per row)
      return 6;
    },
    addAnswer() {
      this.answersList.push({});
    },
    removeAnswer() {
      this.answersList.pop();
    },
  },
  validations() {
    if (this.field.field === "json") {
      return {
        jsonValue: {
          validJson
        }
      };
    }
    return {};
  },
};
</script>
<style lang="scss">
.empty-place {
  width: 41px;
}
</style>
